// src/features/bookings/hooks/useBookings.js
// ✅ مع Pagination — يجلب صفحة بصفحة بدلاً من كل البيانات دفعة واحدة

import { useState, useCallback, useMemo, useEffect } from 'react';
import { fetchBookings, updateBookingStatus, fetchSettings } from '../../../api';

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    start_date: toYmd(new Date(now.getFullYear(), now.getMonth(), 1)),
    end_date:   toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const getDefaultFilters = () => ({
  client_name:  '',
  booking_type: '',
  status:       'all',
  ...getCurrentMonthRange(),
});

const DEFAULT_SORT = {
  key:       'booking_date',
  direction: 'desc',
};

const PAGE_SIZE = 20;

const useBookings = () => {
  const [bookings, setBookings]       = useState([]);
  const [statistics, setStatistics]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [filters, setFilters]         = useState(getDefaultFilters);
  const [sortConfig, setSortConfig]   = useState(DEFAULT_SORT);

  // تطبيق الفلتر الافتراضي المحفوظ مرة واحدة عند أول تحميل
  useEffect(() => {
    fetchSettings()
      .then(res => {
        const savedStatus = res.data?.default_filters?.bookings?.status;
        if (savedStatus) {
          setFilters(prev => ({ ...prev, status: savedStatus }));
        }
      })
      .catch(() => {}); // في حال فشل الجلب نبقى على القيمة الافتراضية
  }, []);

  // ── Pagination state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalCount, setTotalCount]   = useState(0);

  // ─── جلب الحجوزات ────────────────────────────────────────────
  const loadBookings = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchBookings({
        ...filters,
        page,
        page_size: PAGE_SIZE,
      });

      const data = res.data;

      // دعم الاستجابتين: paginated و non-paginated
      if (data.results !== undefined) {
        setBookings(data.results ?? []);
        setTotalPages(data.total_pages ?? 1);
        setTotalCount(data.count ?? data.results?.length ?? 0);
        setCurrentPage(data.current_page ?? page);
      } else {
        // Fallback: API لا يزال يرجع مصفوفة مباشرة
        setBookings(Array.isArray(data) ? data : []);
        setTotalPages(1);
        setTotalCount(Array.isArray(data) ? data.length : 0);
        setCurrentPage(1);
      }

      setStatistics(data.statistics ?? null);
    } catch {
      setError('فشل في تحميل الحجوزات.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ─── تحديث حالة حجز ──────────────────────────────────────────
  const changeBookingStatus = useCallback(async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      await loadBookings(currentPage);
    } catch (err) {
      const msg = err.response?.data?.detail || 'حدث خطأ أثناء تحديث الحالة.';
      setError(msg);
    }
  }, [loadBookings, currentPage]);

  // ─── إدارة الفلاتر ───────────────────────────────────────────
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1); // العودة للصفحة الأولى عند تغيير الفلتر
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(getDefaultFilters());
    setCurrentPage(1);
  }, []);

  // ─── إدارة الفرز ─────────────────────────────────────────────
  const handleSort = useCallback((key, direction) => {
    setSortConfig({ key, direction: direction ?? 'asc' });
  }, []);

  // ─── الانتقال بين الصفحات ─────────────────────────────────────
  const goToPage = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadBookings(page);
  }, [totalPages, loadBookings]);

  // ─── الحجوزات بعد الفرز (فرز محلي للصفحة الحالية) ────────────
  const sortedBookings = useMemo(() => {
    const sorted = [...bookings];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'client') {
        aVal = a.client?.name ?? '';
        bVal = b.client?.name ?? '';
      } else if (['total_price', 'total_paid', 'remaining_amount'].includes(key)) {
        aVal = parseFloat(aVal ?? 0);
        bVal = parseFloat(bVal ?? 0);
      } else {
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [bookings, sortConfig]);

  return {
    // البيانات
    bookings: sortedBookings,
    statistics,
    loading,
    error,
    // الفلاتر
    filters,
    handleFilterChange,
    clearFilters,
    // الفرز
    sortConfig,
    handleSort,
    // الإجراءات
    loadBookings,
    changeBookingStatus,
    // Pagination
    currentPage,
    totalPages,
    totalCount,
    pageSize: PAGE_SIZE,
    goToPage,
  };
};

export default useBookings;
